import { App, Flex, Form } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
    getApiError,
    getBuildingInfoByAddress,
    getPointListByAddressId,
    postBuildingCreate,
    postMarketV2CreateMarketWithChainPoint,
    postMarketV2CreateMarketWithDistributionPoint,
} from "tm-shared/api";
import { Option, PointType } from "tm-shared/types";

import { API_ERROR_CODE } from "@/constants";
import { useFormDependencies } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { BaseButton } from "@/shared/ui";
import {
    clearPointCreatePageCache,
    setNewPointForm,
    setPointCreatePageDuplicateResolution,
    setPointCreatePageModal,
    setPointCreatePageModalSelectionPayload,
    setPointCreatePageOptionsCache,
} from "@/store/slices/ui";
import { NewPointFormValues } from "@/types/form";
import { PointCreateModal } from "@/types/modals";

import { useSyncRhfWithStore } from "../hooks";
import { PointCreateCapabilities } from "../PointCreate.policy";
import { AddressSection } from "./components/AddressSection";
import { ContragentSection } from "./components/ContragentSection";
import { LocationSection } from "./components/LocationSection";
import { PartnerSection } from "./components/PartnerSection";
import { PointServiceSection } from "./components/PointServiceSection";
import { PointTitleSection } from "./components/PointTitleSection";
import { PointTypeSection } from "./components/PointTypeSection";
import { WarningModal } from "./components/WarningModal";
import { FIELD_FLOW_MAP, type FieldName } from "./CreatePointForm.constants";
import { validateRequiredFields } from "./CreatePointForm.utils";
import { StyledFormWrapper } from "./Styled";

const formId = "create-market-form";

interface CreatePointFormProps {
    capabilities: PointCreateCapabilities;
    partnerOptions: Option[];
}

export const CreatePointForm: FC<CreatePointFormProps> = ({
    capabilities,
    partnerOptions,
}) => {
    // App hooks
    const { notification } = App.useApp();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Store data
    const {
        modalSelectionPayload,
        newPointFormValues,
        optionCache,
        duplicateResolution,
    } = useAppSelector((state) => state.ui.pointCreatePage);

    // Local state
    const [warningModalOpen, setWarningModalOpen] = useState(false);
    const [isCheckingPartnerExternalId, setIsCheckingPartnerExternalId] =
        useState(false);
    const [verifiedPartnerExternalId, setVerifiedPartnerExternalId] = useState<
        string | null
    >(null);

    // Form defaults
    const emptyValues: NewPointFormValues = useMemo(
        () => ({
            partner: "",
            clientExternalPointId: "",
            region: "",
            storage: "",
            contragent: "",
            address: "",
            title: "",
            network: "",
            pointType: null,
        }),
        [],
    );

    const defaultValues: NewPointFormValues = {
        ...emptyValues,
        ...newPointFormValues,
    };

    // Form state
    const newMarketForm = useForm<NewPointFormValues>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues,
    });

    const {
        handleSubmit,
        control,
        resetField,
        setValue,
        reset,
        formState: { errors, isSubmitting },
    } = newMarketForm;

    useSyncRhfWithStore(newMarketForm, setNewPointForm);

    // Handlers
    const handleCloseWarningModal = useCallback(() => {
        setWarningModalOpen(false);
    }, []);

    const onSubmit = useCallback(
        async (data: NewPointFormValues) => {
            const {
                clientExternalPointId,
                address,
                contragent,
                title,
                pointType,
                storage,
                network,
                partner,
            } = data;

            if (!clientExternalPointId && !network && !warningModalOpen) {
                setWarningModalOpen(true);
                return;
            }

            const resolvedParentId = duplicateResolution?.parentId;

            if (!address) {
                notification.error({ title: "Адреса обов'язкова" });

                return;
            }

            if (duplicateResolution?.confirmed !== true && address) {
                try {
                    const { result: pointsFromServer } =
                        await getPointListByAddressId({
                            addressId: address,
                        });

                    if (pointsFromServer.length > 0) {
                        const addressLabel =
                            optionCache.address?.find(
                                (option) => option.value === address,
                            )?.label ?? "";

                        dispatch(
                            setPointCreatePageModal({
                                type: PointCreateModal.DuplicateByAddress,
                                payload: {
                                    points: pointsFromServer,
                                    address: addressLabel,
                                    variant: "createPoint",
                                },
                            }),
                        );

                        return;
                    }
                } catch (error) {
                    const { displayMessage } = getApiError(error);

                    notification.error({
                        title: "Помилка при перевірці адреси",
                        description: displayMessage,
                    });

                    return;
                }
            }

            let buildingId = null;

            try {
                const { result: building } = await getBuildingInfoByAddress({
                    addressId: address,
                });

                buildingId = building.id;
            } catch (error) {
                const { code } = getApiError(error);

                if (code === API_ERROR_CODE.NOT_FOUND) {
                    const { result: createdBuildingId } =
                        await postBuildingCreate({
                            addressId: address,
                        });

                    buildingId = createdBuildingId;
                } else {
                    throw error;
                }
            }

            try {
                switch (pointType) {
                    case PointType.Chains: {
                        const fields = { network, contragent };

                        if (
                            !validateRequiredFields(
                                fields,
                                {
                                    network: "Оберіть мережу",
                                    contragent: "Оберіть контрагента",
                                },
                                notification,
                            )
                        ) {
                            return;
                        }

                        await postMarketV2CreateMarketWithChainPoint({
                            buildingId,
                            contragentId: fields.contragent,
                            networkId: fields.network,
                            title,
                            parentId: resolvedParentId,
                        });

                        break;
                    }
                    case PointType.Distribution: {
                        const fields = {
                            storage,
                            clientExternalPointId,
                            contragent,
                            partner,
                        };

                        if (
                            !validateRequiredFields(
                                fields,
                                {
                                    storage: "Оберіть склад",
                                    clientExternalPointId:
                                        "Вкажіть ID торгової точки партнера",
                                    contragent: "Оберіть контрагента",
                                    partner: "Оберіть партнера",
                                },
                                notification,
                            )
                        ) {
                            return;
                        }

                        await postMarketV2CreateMarketWithDistributionPoint({
                            buildingId,
                            contragentId: fields.contragent,
                            clientId: fields.partner,
                            storageId: fields.storage,
                            externalId: fields.clientExternalPointId,
                            title,
                            parentId: resolvedParentId,
                        });

                        break;
                    }

                    default:
                        notification.error({
                            title: "Оберіть тип маркету",
                        });

                        return;
                }

                reset(emptyValues);
                dispatch(setPointCreatePageDuplicateResolution(null));
                dispatch(clearPointCreatePageCache());
                navigate(-1);
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    title: "Помилка при створенні маркету",
                    description: displayMessage,
                });
            }
        },
        [
            notification,
            dispatch,
            duplicateResolution?.confirmed,
            duplicateResolution?.parentId,
            emptyValues,
            navigate,
            optionCache.address,
            reset,
            warningModalOpen,
        ],
    );

    // Field dependencies
    const dependencyManager = useFormDependencies(FIELD_FLOW_MAP);

    const resetBelow = useCallback(
        (fieldName: FieldName) => {
            const fieldsToClear = dependencyManager.getFieldsToReset(fieldName);

            if (fieldName === "address" || fieldsToClear.includes("address")) {
                dispatch(setPointCreatePageDuplicateResolution(null));
            }

            fieldsToClear.forEach((name) => {
                resetField(name, { defaultValue: emptyValues[name] });
            });
        },
        [dependencyManager, dispatch, emptyValues, resetField],
    );

    // Active state
    const watchedValues = useWatch({ control });

    const activeMap = useMemo(() => {
        const nextActiveMap = dependencyManager.computeActiveMap(
            watchedValues,
            {
                clientExternalPointId: (value) => {
                    const okLength = Number(value?.length) >= 3;

                    if (!okLength) return false;
                    if (isCheckingPartnerExternalId) return false;
                    if (errors.clientExternalPointId) return false;

                    return true;
                },
            },
        );

        if (watchedValues.pointType === PointType.Distribution) {
            return {
                ...nextActiveMap,
                region:
                    nextActiveMap.region &&
                    verifiedPartnerExternalId ===
                        watchedValues.clientExternalPointId,
            };
        }

        return nextActiveMap;
    }, [
        dependencyManager,
        watchedValues,
        isCheckingPartnerExternalId,
        verifiedPartnerExternalId,
        errors.clientExternalPointId,
    ]);

    // Modal effects
    useEffect(() => {
        if (modalSelectionPayload) {
            const { fieldName, option, value } = modalSelectionPayload;

            setValue(fieldName, value);
            resetBelow(fieldName);

            if (option) {
                dispatch(
                    setPointCreatePageOptionsCache({
                        fieldName,
                        options: [option],
                    }),
                );
            }

            dispatch(setPointCreatePageModalSelectionPayload(null));
        }
    }, [dispatch, modalSelectionPayload, resetBelow, setValue]);

    useEffect(() => {
        if (duplicateResolution?.confirmed === true) {
            void handleSubmit(onSubmit)();
        }
    }, [duplicateResolution?.confirmed, handleSubmit, onSubmit]);

    return (
        <>
            <FormProvider {...newMarketForm}>
                <StyledFormWrapper>
                    <Form
                        layout="vertical"
                        onFinish={handleSubmit(onSubmit)}
                        id={formId}
                        disabled={isSubmitting}
                        style={{ width: "100%" }}
                    >
                        <Flex vertical gap={16}>
                            <PointTitleSection
                                activeMap={activeMap}
                                resetBelow={resetBelow}
                            />

                            <PointTypeSection
                                activeMap={activeMap}
                                resetBelow={resetBelow}
                            />

                            <PartnerSection
                                canEditPartner={capabilities.canEditPartner}
                                activeMap={activeMap}
                                resetBelow={resetBelow}
                                setIsCheckingPartnerExternalId={
                                    setIsCheckingPartnerExternalId
                                }
                                setVerifiedPartnerExternalId={
                                    setVerifiedPartnerExternalId
                                }
                                isCheckingPartnerExternalId={
                                    isCheckingPartnerExternalId
                                }
                                partners={partnerOptions}
                            />

                            <LocationSection
                                activeMap={activeMap}
                                resetBelow={resetBelow}
                            />

                            <ContragentSection
                                activeMap={activeMap}
                                resetBelow={resetBelow}
                            />

                            <AddressSection
                                activeMap={activeMap}
                                resetBelow={resetBelow}
                            />

                            <PointServiceSection
                                activeMap={activeMap}
                                resetBelow={resetBelow}
                            />
                        </Flex>

                        <Flex style={{ marginTop: "auto" }}>
                            <BaseButton
                                size="large"
                                type="primary"
                                htmlType="submit"
                                loading={isSubmitting}
                                disabled={
                                    !!errors.clientExternalPointId ||
                                    isCheckingPartnerExternalId
                                }
                            >
                                Зберегти
                            </BaseButton>
                        </Flex>
                    </Form>
                </StyledFormWrapper>
            </FormProvider>

            <WarningModal
                open={warningModalOpen}
                onClose={handleCloseWarningModal}
                formId={formId}
                loading={isSubmitting}
            />
        </>
    );
};
